export interface LinkField {
  id: string
  url: string
  linktype: string
  fieldtype: string
  cached_url: string
  target: string
}

// generate URLs for links
export function linkUrl(link: LinkField): string {
  if (!link) return ''

  if (link?.linktype === 'url' && (link?.url === undefined || link?.url === '')) return ''

  // if linktype is 'email', return mailto link
  if (link?.linktype === 'email') {
    return `mailto:${link.url}`
  }

  // if linktype is 'url' and cached_url begins with www. then add https://
  if (link?.linktype === 'url' && link.cached_url.startsWith('www.')) {
    return `https://${link.cached_url}`
  }

  //if linktype is story we want to ensure that it always starts with /
  if (link?.linktype == 'story') {
    // strip out en-nz/ from cached_url
    let updateLink = link.cached_url.replace('en-nz/', '')
    // check if cached_url starts with /
    if (updateLink.startsWith('/')) return updateLink

    return `/${updateLink}`
  }

  return link?.cached_url
}

export type CardLinkLocale = 'default' | 'en-nz'

/**
x * Resolves locale for service card link validation.
 * Distinguishes by domain: findex.co.nz → NZ, findex.com.au (or other) → AU.
 * Also: path /en-nz (local dev) or NEXT_PUBLIC_NEXT_LOCATION=en-nz as fallback.
 */
export function getCardLinkLocale(
  pathname: string | null,
  hostname?: string | null
): CardLinkLocale {
  // Check domain first (production): findex.co.nz → NZ
  if (typeof hostname === 'string' && hostname.includes('findex.co.nz')) return 'en-nz'
  // Check path (local dev): /en-nz/... → NZ
  if (pathname != null && pathname.startsWith('/en-nz')) return 'en-nz'
  // Fallback to env var (for edge cases or server-side)
  return process.env.NEXT_PUBLIC_NEXT_LOCATION === 'en-nz' ? 'en-nz' : 'default'
}

/**
 * Returns true if the URL is a valid destination for a service card link.
 * Used to make cards non-clickable when the locale has no link (e.g. NZ missing link while AU has it).
 * Empty, root, and locale-root URLs are treated as "no link".
 * When locale is 'en-nz', only URLs that start with '/en-nz/' are valid (avoids using AU fallback link from CMS).
 */
export function isValidCardLink(url: string | undefined | null, locale?: CardLinkLocale): boolean {
  if (url == null || typeof url !== 'string') return false
  const trimmed = url.trim()
  if (trimmed === '') return false
  if (trimmed === '/' || trimmed === '/en-nz' || trimmed === '/en-nz/') return false
  // On NZ pages, only accept links that are explicitly NZ (have en-nz prefix). Reject AU-style paths from CMS fallback.
  if (locale === 'en-nz' && !trimmed.startsWith('/en-nz/')) return false
  return true
}
